import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { API_URL } from '../../lib/constants';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

interface FailedFile {
  key: string;
  error: string;
}

interface Extraction {
  status: 'processing' | 'completed' | 'failed' | 'not_started';
  pipelineType?: 'extract' | 'onboard';
  ingestionStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  folderName: string;
  tenantId: string;
  totalFiles: number;
  completedFiles?: string[];
  failedFiles?: FailedFile[];
  startedAt?: string;
  updatedAt?: string;
}

const IngestionTab = () => {
  const { userInfo } = useUserInfoContext();

  const [tenantId, setTenantId] = useState('');
  const [folderName, setFolderName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // Advanced Database Connection Options
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [neo4jUri, setNeo4jUri] = useState('');
  const [neo4jUser, setNeo4jUser] = useState('');
  const [neo4jPassword, setNeo4jPassword] = useState('');
  const [qdrantUrl, setQdrantUrl] = useState('');
  const [qdrantApiKey, setQdrantApiKey] = useState('');

  const [sourceFolders, setSourceFolders] = useState<Array<{ label: string; path: string }>>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [defaults, setDefaults] = useState<{ neo4jUri?: string; neo4jUser?: string; qdrantUrl?: string } | null>(null);

  const [extractions, setExtractions] = useState<Extraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const fetchSourceFolders = useCallback(async () => {
    if (!userInfo?.accessToken) return;
    setLoadingFolders(true);
    try {
      const response = await fetch(`${API_URL}/extract/source-folders`, {
        headers: {
          Authorization: `Bearer ${userInfo.accessToken}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setSourceFolders(result.folders || []);
        setDefaults(result.defaults || null);
      }
    } catch (err) {
      console.error('Error fetching source folders', err);
    } finally {
      setLoadingFolders(false);
    }
  }, [userInfo?.accessToken]);

  useEffect(() => {
    fetchSourceFolders();
  }, [fetchSourceFolders]);

  const fetchExtractions = useCallback(async (silent = false) => {
    if (!userInfo?.accessToken) return;
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/extract/all`, {
        headers: {
          Authorization: `Bearer ${userInfo.accessToken}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setExtractions(result.extractions || []);
        setLastUpdated(new Date());
      } else {
        console.error('Failed to fetch extractions', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching extractions', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userInfo?.accessToken]);

  useEffect(() => {
    fetchExtractions();
  }, [fetchExtractions]);

  const fetchExtractionsRef = useRef(fetchExtractions);
  useEffect(() => {
    fetchExtractionsRef.current = fetchExtractions;
  }, [fetchExtractions]);

  useEffect(() => {
    if (!autoRefresh) return;

    const hasActive = extractions.some(
      (e) => e.status === 'processing' || e.ingestionStatus === 'processing'
    );
    const intervalTime = hasActive ? 5000 : 30000;

    const interval = setInterval(() => {
      fetchExtractionsRef.current(true);
    }, intervalTime);

    return () => clearInterval(interval);
  }, [autoRefresh, extractions]);

  const handleTrigger = async (type: 'extract' | 'onboard') => {
    if (!tenantId.trim() || !folderName.trim()) {
      setAlert({
        message: 'Please fill in both Tenant ID and Folder Path fields.',
        severity: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    setAlert(null);

    const url = type === 'extract' ? `${API_URL}/extract` : `${API_URL}/onboard/tenant`;
    try {
      const payload: any = {
        tenantId: tenantId.trim(),
        folderName: folderName.trim(),
      };

      if (type === 'onboard' && (neo4jUri.trim() || qdrantUrl.trim())) {
        if (neo4jUri.trim()) {
          payload.neo4jUri = neo4jUri.trim();
          payload.neo4jUser = neo4jUser.trim() || undefined;
          payload.neo4jPassword = neo4jPassword.trim() || undefined;
        }
        if (qdrantUrl.trim()) {
          payload.qdrantUrl = qdrantUrl.trim();
          payload.qdrantApiKey = qdrantApiKey.trim() || undefined;
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo?.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setAlert({
          message: `${
            type === 'extract' ? 'Extraction' : 'Onboarding'
          } pipeline successfully started for tenant "${tenantId.trim()}" and folder "${folderName.trim()}".`,
          severity: 'success',
        });
        setTenantId('');
        setFolderName('');
        setNeo4jUri('');
        setNeo4jUser('');
        setNeo4jPassword('');
        setQdrantUrl('');
        setQdrantApiKey('');
        setShowAdvanced(false);
        fetchExtractions();
      } else {
        setAlert({
          message: `Error: ${data.error || 'Failed to start pipeline.'}`,
          severity: 'error',
        });
      }
    } catch (err: any) {
      setAlert({
        message: `Network error: ${err.message || 'Unknown error occurred'}`,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getStatusChip = (status: Extraction['status']) => {
    switch (status) {
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'failed':
        return <Chip label="Failed" color="error" size="small" />;
      case 'processing':
        return <Chip label="Extracting" color="info" size="small" sx={{ animation: 'pulse 1.5s infinite' }} />;
      default:
        return <Chip label="Not Started" color="default" size="small" />;
    }
  };

  const getPipelineTypeChip = (type?: string) => {
    if (type === 'onboard') {
      return <Chip label="Full Onboarding" color="primary" variant="outlined" size="small" />;
    }
    return <Chip label="Extraction Only" color="default" variant="outlined" size="small" />;
  };

  const getIngestionStatusChip = (status?: string, type?: string) => {
    if (type !== 'onboard') {
      return <Typography variant="body2" color="text.secondary" align="center">—</Typography>;
    }
    switch (status) {
      case 'completed':
        return <Chip label="Ingested" color="success" size="small" />;
      case 'failed':
        return <Chip label="Failed" color="error" size="small" />;
      case 'processing':
        return <Chip label="Ingesting" color="info" size="small" sx={{ animation: 'pulse 1.5s infinite' }} />;
      default:
        return <Chip label="Pending" color="default" size="small" />;
    }
  };

  return (
    <Box p={3} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4" gutterBottom>
        Onboarding & Extraction Management
      </Typography>

      {/* Alert Banner */}
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      )}

      {/* Section 1: Trigger Form */}
      <Card component={Paper} elevation={1}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Trigger New Onboarding or Extraction
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Start a background extraction pipeline or trigger a full onboarding (extract from source, convert, chunk, embed, and load into Qdrant/Neo4j).
            </Typography>
          </Box>

          {/* S3 Source Folders Selectable Chips */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              S3 Source Folders: {loadingFolders ? '(Loading...)' : ''}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {sourceFolders.map((folder) => (
                <Chip
                  key={folder.path}
                  label={folder.label}
                  size="small"
                  onClick={() => {
                    setFolderName(folder.path);
                    // Extract tenant ID as first segment of path (e.g. "LOTE/books" -> "LOTE")
                    const parts = folder.label.split('/');
                    if (parts[0]) {
                      setTenantId(parts[0]);
                    }
                  }}
                  variant="outlined"
                  clickable
                  color="primary"
                />
              ))}
              {!loadingFolders && sourceFolders.length === 0 && (
                <Typography variant="caption" color="text.secondary">No root folders found in source bucket.</Typography>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Tenant ID"
                placeholder="e.g. LOTE, acme"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={isSubmitting}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="S3 Folder Path"
                placeholder="e.g. LOTE/LOTE Files Batch 2/Books"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                disabled={isSubmitting}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleTrigger('extract')}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                sx={{ flex: 1, minWidth: '150px' }}
              >
                Extract Only
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTrigger('onboard')}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                sx={{ flex: 1, minWidth: '150px' }}
              >
                Full Onboarding
              </Button>
            </Grid>
          </Grid>

          {/* Database Connection Overrides Collapsible Accordion */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
            >
              {showAdvanced ? 'Hide Advanced Connection Options ▾' : 'Show Advanced Connection Options ▸'}
            </Button>

            <Collapse in={showAdvanced}>
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Database Connection Overrides (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Neo4j URI"
                      placeholder={defaults?.neo4jUri || "bolt://localhost:7687"}
                      helperText={defaults?.neo4jUri ? `Default: ${defaults.neo4jUri}` : ""}
                      value={neo4jUri}
                      onChange={(e) => setNeo4jUri(e.target.value)}
                      disabled={isSubmitting}
                      size="small"
                      inputProps={{ autoComplete: 'new-password' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Neo4j User"
                      placeholder={defaults?.neo4jUser || "neo4j"}
                      helperText={defaults?.neo4jUser ? `Default: ${defaults.neo4jUser}` : ""}
                      value={neo4jUser}
                      onChange={(e) => setNeo4jUser(e.target.value)}
                      disabled={isSubmitting}
                      size="small"
                      inputProps={{ autoComplete: 'new-password' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Neo4j Password"
                      type="password"
                      placeholder="••••••••"
                      value={neo4jPassword}
                      onChange={(e) => setNeo4jPassword(e.target.value)}
                      disabled={isSubmitting}
                      size="small"
                      inputProps={{ autoComplete: 'new-password' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Qdrant URL"
                      placeholder={defaults?.qdrantUrl || "http://localhost:6333"}
                      helperText={defaults?.qdrantUrl ? `Default: ${defaults.qdrantUrl}` : ""}
                      value={qdrantUrl}
                      onChange={(e) => setQdrantUrl(e.target.value)}
                      disabled={isSubmitting}
                      size="small"
                      inputProps={{ autoComplete: 'new-password' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Qdrant API Key"
                      type="password"
                      placeholder="••••••••"
                      value={qdrantApiKey}
                      onChange={(e) => setQdrantApiKey(e.target.value)}
                      disabled={isSubmitting}
                      size="small"
                      inputProps={{ autoComplete: 'new-password' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {/* Section 2: Monitoring List */}
      <Card component={Paper} elevation={1}>
        <CardContent sx={{ position: 'relative' }}>
          {/* Header & Refresh Controls */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">
              Active & Past Extraction Pipelines
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Auto-refresh (1m)"
              />
              <IconButton onClick={() => fetchExtractions()} disabled={loading} size="small" color="primary">
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Table of Extractions */}
          {loading && extractions.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : extractions.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">No historical extractions found in S3.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell width="40px" />
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Folder Path</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Extraction Progress</TableCell>
                    <TableCell>Extraction Status</TableCell>
                    <TableCell align="center">Ingestion Status</TableCell>
                    <TableCell>Started At</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extractions.map((row) => {
                    const rowId = `${row.tenantId}-${row.folderName}`;
                    const isExpanded = !!expandedRows[rowId];
                    const completedCount = row.completedFiles?.length || 0;
                    const failedCount = row.failedFiles?.length || 0;
                    const totalFiles = row.totalFiles || 0;
                    const processedCount = completedCount + failedCount;

                    // Progress math
                    const percent = totalFiles > 0 ? Math.min(100, Math.round((processedCount / totalFiles) * 100)) : 0;

                    return (
                      <React.Fragment key={rowId}>
                        {/* Main Row */}
                        <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleRow(rowId)}>
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{row.tenantId}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{row.folderName}</TableCell>
                          <TableCell>{getPipelineTypeChip(row.pipelineType)}</TableCell>
                          <TableCell sx={{ minWidth: '150px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: '100%' }}>
                                {row.status === 'processing' && totalFiles === 0 ? (
                                  <LinearProgress variant="indeterminate" color="info" />
                                ) : (
                                  <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    color={row.status === 'failed' ? 'error' : 'primary'}
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {totalFiles > 0 ? `${percent}% (${processedCount}/${totalFiles})` : row.status === 'processing' ? 'Pending...' : '0/0'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell align="center">{getIngestionStatusChip(row.ingestionStatus, row.pipelineType)}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {row.startedAt ? new Date(row.startedAt).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>

                        {/* Collapsible Detail Row */}
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                  Extraction Pipeline Details
                                </Typography>

                                {/* Database Connection Details */}
                                {row.pipelineType === 'onboard' && (
                                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                        Neo4j Graph Database URI:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {!(row as any).neo4jUri ||
                                         (row as any).neo4jUri === 'default' ||
                                         (row as any).neo4jUri === defaults?.neo4jUri ||
                                         (row as any).neo4jUri === 'bolt://localhost:7687'
                                          ? `${defaults?.neo4jUri || 'bolt://localhost:7687'} (default)`
                                          : (row as any).neo4jUri}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                        Qdrant Vector Database URL:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {!(row as any).qdrantUrl ||
                                         (row as any).qdrantUrl === 'default' ||
                                         (row as any).qdrantUrl === defaults?.qdrantUrl ||
                                         (row as any).qdrantUrl === 'http://localhost:6333'
                                          ? `${defaults?.qdrantUrl || 'http://localhost:6333'} (default)`
                                          : (row as any).qdrantUrl}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}

                                <Grid container spacing={3}>
                                  {/* Failures List */}
                                  <Grid item xs={12} md={failedCount > 0 && completedCount > 0 ? 6 : 12}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdf2f2', borderColor: '#fde8e8' }}>
                                      <Typography variant="subtitle2" color="error" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Failed Files ({failedCount})
                                      </Typography>
                                      {row.failedFiles && row.failedFiles.length > 0 ? (
                                        <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                                          <List dense>
                                            {row.failedFiles.map((f, i) => (
                                              <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                                                <ListItemText
                                                  primary={
                                                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                                                      {f.key}
                                                    </Typography>
                                                  }
                                                  secondary={
                                                    <Typography variant="caption" color="error">
                                                      {f.error}
                                                    </Typography>
                                                  }
                                                />
                                              </ListItem>
                                            ))}
                                          </List>
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          No failed files.
                                        </Typography>
                                      )}
                                    </Paper>
                                  </Grid>

                                  {/* Successes List */}
                                  {completedCount > 0 && (
                                    <Grid item xs={12} md={failedCount > 0 ? 6 : 12}>
                                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f6fdf9', borderColor: '#e6f4ea' }}>
                                        <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                                          Successfully Processed Files ({completedCount})
                                        </Typography>
                                        {row.completedFiles && row.completedFiles.length > 0 ? (
                                          <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                                            <List dense>
                                              {row.completedFiles.map((filename, i) => (
                                                <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                                                  <ListItemText
                                                    primary={
                                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                        {filename}
                                                      </Typography>
                                                    }
                                                  />
                                                </ListItem>
                                              ))}
                                            </List>
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No files successfully processed yet.
                                          </Typography>
                                        )}
                                      </Paper>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default IngestionTab;
